import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core';
import { ActionSheetButton, ActionSheetController, IonButton, IonSpinner } from '@ionic/angular/standalone';

// When removing a reaction, we use this id to allow tracking
// of when reaction is removed allowing item updates to find
// changes on removal
const RemovedReactionId = 'removed';

@Component({
  selector: 'app-item-reaction',
  templateUrl: './item-reaction.component.html',
  styleUrls: ['./item-reaction.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass, IonButton, IonSpinner]
})
export class ItemReactionComponent implements OnChanges {
  @Input() reaction: { id: string; description: string; inProgress: boolean };
  @Input() allowedReactions: { id: string; description: string }[];
  @Output() reactWith = new EventEmitter<string>();

  knownReactions: Record<string, { icon: string }> = {
    'like': { icon: 'assets/icon/like-reacted.svg' },
    'heart': { icon: 'assets/icon/heart-reacted.svg' },
    'laugh': { icon: 'assets/icon/laughing-reacted.svg' },
    'tick': { icon: 'assets/icon/tick-reacted.svg' },
    'clapping': { icon: 'assets/icon/clapping-reacted.svg' },
    'waving': { icon: 'assets/icon/waving-reacted.svg' },
    'party-popper': { icon: 'assets/icon/party-popper-reacted.svg' },
    'idea': { icon: 'assets/icon/idea-reacted.svg' },
    'cross': { icon: 'assets/icon/cross-reacted.svg' }
  };
  unknownReactionIcon = 'assets/icon/unknown-icon-reacted.svg';

  actionSheetButtons: (string | ActionSheetButton)[];
  buttonConfig: { icon: string; disabled: boolean; reacted: boolean };
  constructor(public actionSheetController: ActionSheetController) {}
  ngOnChanges(changes: SimpleChanges): void {
    const canReact = this.allowedReactions?.length > 0;
    const knownCurrentReaction = this.knownReactions[this.reaction?.id];
    const icon =
      this.reaction && this.reaction.id !== RemovedReactionId
        ? knownCurrentReaction
          ? knownCurrentReaction.icon
          : this.unknownReactionIcon
        : canReact
          ? this.allowedReactions?.length === 1
            ? 'assets/icon/single-reaction-app.svg'
            : 'assets/icon/multiple-reactions-app.svg'
          : 'assets/icon/reactions-disabled-app.svg';
    this.buttonConfig = { icon: icon, disabled: !canReact, reacted: !!knownCurrentReaction };
    this.actionSheetButtons = this.allowedReactions.map((r) => {
      const knownReaction = this.knownReactions[r.id];
      return {
        text: r.description,
        cssClass: this.reaction?.id === r.id ? 'current' : undefined,
        icon: knownReaction?.icon ?? this.unknownReactionIcon,
        handler: () => this.onReaction(this.reaction?.id === r.id ? RemovedReactionId : r.id)
      };
    });
  }

  onReaction(reactionId: string): void {
    this.reactWith.emit(reactionId);
  }

  async onClick(): Promise<void> {
    if (this.allowedReactions?.length === 1) {
      this.reactWith.emit(this.reaction?.id === 'like' ? RemovedReactionId : 'like');
    } else {
      const actionSheet = await this.actionSheetController.create({
        cssClass: 'reaction-sheet',
        header: this.reaction && this.reaction.id !== RemovedReactionId ? 'Edit reaction' : 'Select reaction',
        translucent: true,
        buttons: this.actionSheetButtons,
        mode: 'md'
      });
      await actionSheet.present();
    }
  }
}
